import { useEffect, type CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { useGameStore } from '../stores/gameStore';
import {
  BOOST_PERCENT,
  GameMode,
  MATCH_ENTRY_FEE,
} from '@elmental/shared';
import { haptic } from '../services/telegram';
import { startMatchmaking } from '../services/gameService';
import { playerDisplayName } from '../services/playerProfile';
import { currencyForUser, formatCurrencyAmount } from '../services/economy';
import { warmModeImageCache } from '../services/assetCache';
import { SwordsIcon } from '../components/icons/SwordsIcon';
import { TrophyIcon } from '../components/icons/TrophyIcon';
import { InfoIcon } from '../components/icons/InfoIcon';
import { GearIcon } from '../components/icons/GearIcon';
import { BoltIcon } from '../components/icons/BoltIcon';
import { EarthIcon } from '../components/icons/EarthIcon';
import { WaterIcon } from '../components/icons/WaterIcon';
import { TopUpOverlay } from '../components/topUp/TopUpOverlay';
import classicModePortraitSm from '../assets/modes/mode-classic-portrait-sm.jpg';
import classicModePortraitMd from '../assets/modes/mode-classic-portrait-md.jpg';
import hardcoreModePortraitSm from '../assets/modes/mode-hardcore-portrait-sm.jpg';
import hardcoreModePortraitMd from '../assets/modes/mode-hardcore-portrait-md.jpg';
import chaosModePortraitSm from '../assets/modes/mode-chaos-portrait-sm.jpg';
import chaosModePortraitMd from '../assets/modes/mode-chaos-portrait-md.jpg';

const GAME_MODES = [
  {
    id: GameMode.Classic,
    label: 'Classic',
    tagline: 'Balanced flow',
    color: '#3b82f6',
    accentBorder: 'oklch(72% 0.18 249 / 0.95)',
    accentGlow: 'oklch(68% 0.2 249 / 0.72)',
    accentInset: 'oklch(70% 0.17 249 / 0.28)',
    titleSelected: 'oklch(86% 0.1 244)',
    taglineSelected: 'oklch(82% 0.075 244)',
    art: classicModePortraitSm,
    artSrcSet: `${classicModePortraitSm} 192w, ${classicModePortraitMd} 288w`,
  },
  {
    id: GameMode.Hardcore,
    label: 'Hardcore',
    tagline: 'No regen',
    color: '#ef4444',
    accentBorder: 'oklch(70% 0.21 29 / 0.95)',
    accentGlow: 'oklch(66% 0.23 29 / 0.72)',
    accentInset: 'oklch(68% 0.19 29 / 0.28)',
    titleSelected: 'oklch(84% 0.105 32)',
    taglineSelected: 'oklch(80% 0.085 32)',
    art: hardcoreModePortraitSm,
    artSrcSet: `${hardcoreModePortraitSm} 192w, ${hardcoreModePortraitMd} 288w`,
  },
  {
    id: GameMode.Chaos,
    label: 'Chaos',
    tagline: 'Storm rolls',
    color: '#a855f7',
    accentBorder: 'oklch(74% 0.22 306 / 0.95)',
    accentGlow: 'oklch(70% 0.24 306 / 0.74)',
    accentInset: 'oklch(72% 0.2 306 / 0.3)',
    titleSelected: 'oklch(86% 0.105 306)',
    taglineSelected: 'oklch(82% 0.095 306)',
    art: chaosModePortraitSm,
    artSrcSet: `${chaosModePortraitSm} 192w, ${chaosModePortraitMd} 288w`,
  },
] as const;

const MODE_IMAGE_CACHE_URLS = [
  classicModePortraitSm,
  classicModePortraitMd,
  hardcoreModePortraitSm,
  hardcoreModePortraitMd,
  chaosModePortraitSm,
  chaosModePortraitMd,
] as const;

export function HomeScreen() {
  const {
    telegramUser,
    elmBalance,
    seasonPoints,
    rating,
    stats,
    gameMode,
    boostEnabled,
    setGameMode,
    setBoostEnabled,
    setScreen,
  } = useGameStore();
  const displayName = playerDisplayName(telegramUser);

  const winRate =
    stats.wins + stats.losses > 0
      ? Math.round((stats.wins / (stats.wins + stats.losses)) * 100)
      : 0;

  const boostStake = boostEnabled ? Math.ceil((MATCH_ENTRY_FEE * BOOST_PERCENT) / 100) : 0;
  const matchCost = MATCH_ENTRY_FEE + boostStake;
  const canAffordMatch = elmBalance >= matchCost;
  const currency = currencyForUser(telegramUser);
  const isTelegramBalance = telegramUser?.source === 'telegram';

  useEffect(() => {
    warmModeImageCache(MODE_IMAGE_CACHE_URLS);
  }, []);

  const handlePlay = () => {
    if (!canAffordMatch) {
      haptic.error();
      return;
    }
    haptic.medium();
    void startMatchmaking();
  };

  return (
    <div className="game-home-stage flex flex-col h-full overflow-y-auto scrollbar-hide">
      <div className="flex min-h-full flex-col gap-4 p-4 pb-7">

        {/* Header bar */}
        <motion.div
          className="flex items-center justify-between"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <button
            type="button"
            data-nav
            className="arena-player-chip flex items-center gap-2"
            aria-label="Open profile"
            onClick={() => setScreen('profile')}
          >
            {/* Avatar */}
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center text-xl font-black overflow-hidden border-2"
              style={{
                background: 'linear-gradient(135deg, oklch(88% 0.12 87), oklch(68% 0.135 238))',
                borderColor: 'oklch(100% 0 0 / 0.2)',
                boxShadow: '0 10px 22px oklch(3% 0.02 252 / 0.42)',
              }}
            >
              {telegramUser?.photo_url ? (
                <img
                  src={telegramUser.photo_url}
                  alt="avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                displayName.charAt(0).toUpperCase()
              )}
            </div>
            <div>
              <div className="font-bold text-sm text-text-primary leading-tight">
                {displayName}
              </div>
              <div className="text-[14px] text-text-secondary flex items-center gap-1">
                <TrophyIcon size={14} className="text-gold" /> {rating} Rating
              </div>
            </div>
          </button>

          {/* Top right actions */}
          <div className="flex gap-2">
            <button
              data-nav
              className="hud-icon-button flex items-center justify-center"
              aria-label="Open information"
              onClick={() => setScreen('information')}
            >
              <InfoIcon size={18} className="text-text-secondary" />
            </button>
            <button
              data-nav
              className="hud-icon-button flex items-center justify-center"
              aria-label="Open settings"
              onClick={() => setScreen('settings')}
            >
              <GearIcon size={18} className="text-text-secondary" />
            </button>
          </div>
        </motion.div>

        {/* Arena HUD */}
        <motion.div
          className="arena-balance-hud"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
        >
          <div className="text-xs text-text-secondary font-semibold tracking-widest uppercase mb-1">
            {isTelegramBalance ? 'ELM Match Credits' : 'Demo tELM Credits'}
          </div>
          <div className="flex items-center justify-center gap-2">
            <motion.div
              key={elmBalance}
              className="balance-number-inline glow-text-gold text-5xl font-black tabular-nums"
              initial={{ scale: 0.98 }}
              animate={{ scale: [1.02, 1] }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              {elmBalance.toLocaleString()}
            </motion.div>
            <TopUpOverlay />
          </div>
          <div className="text-xs font-semibold text-text-secondary mt-1">match credits</div>

          {/* Win/Loss stats */}
          <div className="flex items-center justify-center gap-2 mt-5">
            <div className="arena-stat-chip text-center">
              <div className="text-lg font-black text-energy-high">{stats.wins}</div>
              <div className="text-xs text-text-secondary">Wins</div>
            </div>
            <div className="arena-stat-chip text-center">
              <div className="text-lg font-black text-energy-low">{stats.losses}</div>
              <div className="text-xs text-text-secondary">Losses</div>
            </div>
            <div className="arena-stat-chip text-center">
              <div className="text-lg font-black text-water-light">{winRate}%</div>
              <div className="text-xs text-text-secondary">Win Rate</div>
            </div>
            <div className="arena-stat-chip text-center">
              <div className="text-lg font-black text-gold">{seasonPoints.toLocaleString()}</div>
              <div className="text-xs text-text-secondary">SP Earned</div>
            </div>
          </div>

        </motion.div>

        <div className="mt-auto flex flex-col gap-4 pt-2">
          {/* Game Mode selector */}
          <motion.div
            className="arena-mode-dock"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <div className="text-xs text-text-secondary font-semibold tracking-widest uppercase mb-3 px-1">
              Game Mode
            </div>
            <div className="grid grid-cols-3 gap-2">
              {GAME_MODES.map((mode) => {
                const isSelected = gameMode === mode.id;
                return (
                  <motion.button
                    key={mode.id}
                    data-nav
                    className={`arena-mode-option${isSelected ? ' arena-mode-option-selected' : ''}`}
                    style={{
                      '--mode-accent-border': mode.accentBorder,
                      '--mode-accent-glow': mode.accentGlow,
                      '--mode-accent-inset': mode.accentInset,
                    } as CSSProperties}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      haptic.light();
                      setGameMode(mode.id);
                    }}
                  >
                    <img
                      src={mode.art}
                      srcSet={mode.artSrcSet}
                      sizes="(max-width: 380px) 29vw, (max-width: 520px) 132px, 156px"
                      width={192}
                      height={256}
                      loading="eager"
                      decoding="async"
                      {...(isSelected ? { fetchpriority: 'high' } : {})}
                      alt=""
                      aria-hidden="true"
                      className="arena-mode-art"
                    />
                    <span className="arena-mode-scrim" />
                    <span className="arena-mode-caption">
                      <span
                        className="arena-mode-title"
                        style={{
                          color: isSelected ? mode.titleSelected : 'oklch(94% 0.016 86)',
                          textShadow: isSelected
                            ? `0 0 24px ${mode.accentGlow}, 0 0 10px ${mode.accentBorder}, 0 2px 8px oklch(3% 0.02 252 / 0.9)`
                            : undefined,
                        }}
                      >
                        {mode.label}
                      </span>
                      <span
                        className="arena-mode-tagline"
                        style={{
                          color: isSelected ? mode.taglineSelected : undefined,
                          textShadow: isSelected
                            ? `0 0 20px ${mode.accentGlow}, 0 0 8px ${mode.accentBorder}, 0 2px 7px oklch(3% 0.02 252 / 0.88)`
                            : undefined,
                        }}
                      >
                        {mode.tagline}
                      </span>
                    </span>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>

          {/* Energy Boost toggle */}
          <motion.div
            className="arena-toggle-strip flex items-center justify-between"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-3">
              <BoltIcon size={24} className="text-gold" />
              <div>
                <div className="font-bold text-sm text-text-primary">Energy Boost</div>
                <div className="text-xs text-text-secondary">
                  Start with +20 energy (+10% match cost)
                </div>
              </div>
            </div>
            <motion.button
              data-nav
              className="relative w-13 h-7 rounded-full transition-colors"
              style={{
                width: '52px',
                height: '28px',
                background: boostEnabled
                  ? 'linear-gradient(90deg, oklch(43% 0.16 252), oklch(68% 0.135 238))'
                  : 'oklch(10% 0.035 252 / 0.7)',
                border: boostEnabled ? '1px solid oklch(68% 0.135 238 / 0.74)' : '1px solid oklch(76% 0.026 86 / 0.26)',
                boxShadow: boostEnabled ? '0 10px 18px oklch(58% 0.16 245 / 0.28)' : '0 4px 10px oklch(3% 0.02 252 / 0.36) inset',
              }}
              onClick={() => {
                haptic.selection();
                setBoostEnabled(!boostEnabled);
              }}
            >
              <motion.div
                className="absolute top-0.5 w-6 h-6 rounded-full"
                style={{
                  background: boostEnabled ? 'oklch(14% 0.045 252)' : 'oklch(59% 0.035 86)',
                  bottom: '2px',
                }}
                animate={{ x: boostEnabled ? 24 : 2 }}
                transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
              />
            </motion.button>
          </motion.div>

          {/* PLAY button */}
          <motion.div
            className="flex flex-col items-center gap-2 pt-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <motion.button
              data-nav
              className={`btn-play w-full text-center ${!canAffordMatch ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={!canAffordMatch ? { animation: 'none', filter: 'grayscale(0.5)' } : {}}
              whileTap={canAffordMatch ? { scale: 0.96 } : {}}
              onClick={handlePlay}
            >
              {canAffordMatch ? (
                <span className="flex items-center gap-2 justify-center">
                  <SwordsIcon size={22} />
                  PLAY NOW
                </span>
              ) : (
                `NOT ENOUGH ${currency}`
              )}
            </motion.button>
            <div className="arena-footnote-pill text-xs font-semibold text-text-secondary text-center">
              {canAffordMatch
                ? `Entry fee: ${formatCurrencyAmount(matchCost, currency)} | ${gameMode} mode`
                : `Need ${formatCurrencyAmount(matchCost, currency)} (have ${formatCurrencyAmount(elmBalance, currency)})`}
            </div>
          </motion.div>
        </div>

        {/* Floating element decorations as CSS particles */}
        <div className="absolute top-20 right-4 opacity-5 pointer-events-none">
          <EarthIcon size={32} className="text-earth-light" />
        </div>
        <div className="absolute top-40 left-2 opacity-5 pointer-events-none">
          <WaterIcon size={28} className="text-water-light" />
        </div>
      </div>

    </div>
  );
}
